/**
 * @file: hooks工具集
 * @author: chenyingliao <chenyingliao@baidu.com>
 * @Date: 2023-10-17 17:39:26
 * @LastEditors: chenyingliao <chenyingliao@baidu.com>
 * @LastEditTime: 2024-04-09 18:48:12
 * @FilePath: /console-znzs/new_src/hooks/index.js
 */
import Vue, {
    getCurrentInstance,
    ref,
    unref,
    watch,
    computed,
    onMounted,
    onBeforeUnmount,
} from 'vue';
import {
    createGlobalState,
    tryOnBeforeUnmount,
    tryOnMounted,
    useCurrentElement,
    watchImmediate,
    useDocumentVisibility,
} from '@vueuse/core';
import {useRoute} from 'vue-router/composables';
import {storeToRefs} from 'pinia';
import {cloneDeep, isNumber, isEmpty, filter, map} from 'lodash-es';
import delegate from 'delegate';
import {Message} from '@baidu/acud-element-ui';
import {api as viewerApi} from 'v-viewer';
import {safeOpenTab, isOffcialDoc, safeOpenDoc} from '@/znzs/new_src/utils/index';
import {handleAfterClick} from '@/znzs/new_src/utils/chat';
import {useImStoreOut} from '@/znzs/new_src/store/imStore';
import {useChatStoreOut} from '@/znzs/new_src/store/chatStore';
import {useTicketStore} from '@/znzs/new_src/store/ticketStore';
import {useCommonStore} from '@/znzs/new_src/store/commonStore';
import {constant, QUESTION_TRIGGER, CHANNEL} from '@/znzs/new_src/constant';
import {beforeChatEnter} from '@/znzs/new_src/router/hooks.js';
import store from '@/znzs/new_src/vuex/index';
import {getAvailableRegion} from '@/znzs/new_src/api/console';
import {userSubmitQuestion} from '@/znzs/src/api/robot';
import {handleAfterUserAction} from '@/znzs/src/utils/chat';

export const trackBehavior = (...args) => Vue.prototype.$trackBehavior(...args);

export const getInstance = () => {
    const vm = getCurrentInstance();
    if (!vm) {
        throw new Error('只能在setup中调用');
    };
    return vm;
};

/**
 * 返回全局store
 * @returns
 */
export const useStore = () => {
    const vm = getCurrentInstance();
    if (!vm) {
        return {state: {}, getters: {}, commit: () => {}, dispatch: () => {}};
    }
    return vm.proxy.$store || {state: {}, getters: {}, commit: () => {}, dispatch: () => {}};
};

export const useRouteParams = () => {
    const vm = getInstance();
    return vm.proxy.$route.params;
};

export const useServiceType = createGlobalState(() => {
    const typeMap = ref({});
    const typeList = computed(() => Object.keys(typeMap.value).map(key => typeMap.value[key]));
    /**
     * 检测SERVICE_TYPE是否存在
     */
    function getservice() {
        if (window.SERVICE_TYPE) {
            typeMap.value = cloneDeep(window.SERVICE_TYPE);
        }
        else {
            setTimeout(() => {
                getservice();
            }, 200);
        }
    }
    getservice();
    return {
        typeMap,
        typeList,
    };
});

// 封装当前可用的Region
export const useRegion = createGlobalState(() => {
    const availableRegion = ref([]);
    const regionMap = ref({});
    const availableRegionList = computed(() => {
        let list = [];
        if (!(isEmpty(availableRegion.value) || isEmpty(regionMap.value))) {
            list = map(
                filter(availableRegion.value, key => regionMap.value[key]),
                key => ({key, value: regionMap.value[key]})
            );
        }
        return list;
    });

    async function getRegionList() {
        try {
            const res = await getAvailableRegion();
            if (res.success) {
                availableRegion.value = res.result;
            }
        }
        finally {}
    }
    // 获取region的中文映射
    function getRegionMap() {
        if (window.REGION) {
            regionMap.value = cloneDeep(window.REGION);
        }
        else {
            setTimeout(() => {
                getRegionMap();
            }, 200);
        }
    }
    getRegionMap();
    getRegionList();

    return {
        regionList: availableRegionList,
        regionMap,
    };
});

/**
 * 检测富文本中的ticket链接
 */
export const useRichTextLink = (enableTicket = true) => {
    const delegation = ref(null);
    const el = useCurrentElement();

    const ticketClick = ref(null);
    const docClick = ref(null);
    const otherClick = ref(null);
    const transformTicket = useTransformTicket({
        useTicketId: false,
    });
    const chatStore = useChatStoreOut();

    const stopDefault = e => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        else {
            e.returnValue = false; // IE
        }
        return false;
    };

    const onHrefClick = e => {
        stopDefault(e);
        const node = e.delegateTarget || e.target || e.srcElement;
        if (!node.href) {
            return;
        }
        if (node.href.includes('#/ticket/create')) {
            if (enableTicket) {
                ticketClick.value = Symbol();
                transformTicket({
                    href: node.href,
                });
            }
        }
        else if (
            !isOffcialDoc(node.href)
            && !node.href.includes('/znzs/knowledge')) {
            otherClick.value = Symbol();
            safeOpenTab(node.href);
        }
        else {
            // 官网文档及对外语料打开
            // 在独立页面默认会冒泡至window，由控制台小窗直接打开
            docClick.value = {
                node,
            };
            chatStore.showLink(node.href);
        }
        e.stopPropagation();
    };
    onMounted(() => {
        delegation.value = delegate(el.value, 'a', 'click', onHrefClick, false);
    });

    onBeforeUnmount(() => {
        if (delegation.value) {
            delegation.value.destroy();
        }
    });

    return {
        docClick,
        otherClick,
        ticketClick,
    };
};
/**
 * 检测富文本中的sup标签点击事件
 * 通过查找参考资源下面的超链进行模拟点击
 */
export const useSupClick = () => {
    const delegation = ref(null);
    const el = useCurrentElement();
    // new_src/utils/typed.js
    const sourceEl = computed(() => el.value && el.value?.querySelector('.source+.order-list'));

    function onClick(e) {
        if (!unref(sourceEl)) {
            return;
        }
        const index = +e.target.innerText;
        if (!isNumber(index) || Number.isNaN(index)) {
            return;
        }
        const list = unref(sourceEl).querySelectorAll('a');
        const target = list[index - 1];
        if (!target) {
            return;
        }
        target.click();
        trackBehavior({
            name: '点击角标',
        });
    }
    onMounted(() => {
        delegation.value = delegate(el.value, 'sup', 'click', onClick, false);
    });

    onBeforeUnmount(() => {
        if (delegation.value) {
            delegation.value.destroy();
        }
    });
};

/**
 * 转工单hook,添加了对im状态的校验
 * 历史记录中触发提单直接打开新标签页
 * @param {*} params
 * @param targetStore 部分场景下无法调用useStore时，从外部直接传入
 * @returns
 */
export function useTransformTicket(params = {}, action = 'transformTicket') {
    const imStore = useImStoreOut();
    const ticketStore = useTicketStore();
    const chatStore = useChatStoreOut();
    const {socketConnected} = storeToRefs(imStore);
    return (newParams = {}) => {
        if (unref(socketConnected)) {
            Message.warning('您当前已进入人工服务环节，如有任何疑问可直接进行反馈');
            return;
        }
        if (chatStore.currentSessionIdIsHistory) {
            safeOpenTab(constant.true.createTicketLinkNew);
        }
        else {
            store.dispatch(action, {...params, ...newParams, checkPermission: ticketStore.checkPermission});
        }
    };
}

export const useImagePreview = () => {
    const rootEl = useCurrentElement();
    let delegation = null;
    tryOnMounted(() => {
        if (!unref(rootEl)) {
            return;
        }
        delegation = delegate(unref(rootEl), 'img', 'click', e => {
            const img = e.target;
            if (!img) {
                return;
            }
            const imgs = rootEl?.value?.querySelectorAll('img') || [];
            let index = 0;
            const images = Array.from(imgs).map((pic, picIndex) => {
                if (pic.src === img.src) {
                    index = picIndex;
                }
                return pic.src;
            });
            viewerApi({
                options: {
                    initialViewIndex: index,
                },
                images,
            });
            e.stopPropagation();
        });
    });

    tryOnBeforeUnmount(() => {
        delegation && delegation.destroy();
    });
};

export function useRenderLoop({onUpdate, minDelay = 10, content, enable = true}) {
    const charQueue = computed(() => unref(content).split(''));
    const renderLength = ref(0);
    const lastUpdateTime = ref(0);
    const animationFrameRef = ref(null);
    function render(currentTime) {
        animationFrameRef.value = null;
        // 还没有收到回复
        if (!charQueue.value.length) {
            animationFrameRef.value = requestAnimationFrame(render);
            return;
        }
        if (currentTime - lastUpdateTime.value < minDelay) {
            animationFrameRef.value = requestAnimationFrame(render);
            return;
        }
        // 更新时间
        lastUpdateTime.value = currentTime;
        const fullContent = unref(content);

        renderLength.value += Math.max(1, Math.floor((fullContent.length - renderLength.value) / 5));
        if (/(\`\`\`mermaid)|(参考资源:)/.test(fullContent.slice(0, renderLength.value))) {
            renderLength.value = fullContent.length;
        }
        // 更新UI
        onUpdate(fullContent.slice(0, renderLength.value));
        if (renderLength.value < fullContent.length) {
            animationFrameRef.value = requestAnimationFrame(render);
        }
    }

    watchImmediate(content, () => {
        if (!animationFrameRef.value && enable) {
            animationFrameRef.value = requestAnimationFrame(render);
        }
    });
    tryOnBeforeUnmount(() => {
        if (animationFrameRef.value) {
            cancelAnimationFrame(animationFrameRef.value);
            animationFrameRef.value = null;
        }
    });
}

/**
 * 切换页面的时候同步最新消息
 */
export function usePageVisibility() {
    const route = useRoute();
    const chatStore = useChatStoreOut();
    const commonStore = useCommonStore();
    const visibility = useDocumentVisibility();

    const visible = computed(() => visibility.value === 'visible');

    /**
 * 激活当前标签页的时候，更新当前页面的状态
 */
    watch(visible, (currentVisible, prevVisible) => {
        if (chatStore.currentSessionIdIsHistory) {
            return;
        }
        if (currentVisible && !prevVisible) {
        // 此处只同步IM状态，暂不同步机器人消息。
        // 如果要同步机器人消息，调用src/vuex/plugin.js的initStore方法
            beforeChatEnter(route);
            commonStore.pageLoading = false;
        }
    });
}

/**
 * 所有点击获取答案的入口，发送的消息会展示到聊天列表里
 * @returns
 */
export function useClickQuestion() {
    const chatStore = useChatStoreOut();
    return (...args) => {
        if (chatStore.currentSessionIdIsHistory) {
            return;
        }
        handleAfterClick(...args);
    };
}

// 通过自主触发卡片的消息，发送的消息不需要显示到聊天列表里
export function useCardQuestion() {
    const commonStore = useCommonStore();
    const chatStore = useChatStoreOut();
    async function cardQuestion(content) {
        if (chatStore.currentSessionIdIsHistory) {
            return;
        }
        const {data} = await userSubmitQuestion({
            trigger: QUESTION_TRIGGER.INPUT,
            channel: CHANNEL,
            content,
            userName: commonStore.user.name,
        });
        handleAfterUserAction(data);
    }

    return cardQuestion;
}